// src/api/features/modules/api.ts
import { request } from "@/api/client";
import { PATHS } from "@/api/constants";
import type {
  Module,
  CreateModuleBody,
  UpdateModuleBody,
  ListModulesQuery,
  PaginatedModules,
} from "./types";

/* =========================
 * Helpers
 * ========================= */
function qs(params: Record<string, unknown>) {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    u.set(k, String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : "";
}

type ApiEnvelope<T> = { ok: boolean; message?: string; data: T; meta?: any };

function unwrap<T>(res: any): T {
  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    return (res as ApiEnvelope<T>).data;
  }
  return res as T;
}

/* =========================
 * URLs (constants)
 * ========================= */
const MODULES_BASE = PATHS.modules.base;

/* =========================
 * Endpoints
 * ========================= */

// 🔹 CREATE
export async function createModule(body: CreateModuleBody): Promise<Module> {
  // Backend generates slug / status etc., FE only sends minimal fields.
  const { slug: _ignoreSlug, isActive: _ignoreActive, ...dto } = body as any;

  const parentId =
    dto.parentId === "" || dto.parentId === undefined
      ? null
      : typeof dto.parentId === "string"
      ? Number(dto.parentId)
      : dto.parentId ?? null;

  const finalBody = {
    name: String(dto.name ?? "").trim(),
    parentId,
  };

  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug("[createModule] payload →", finalBody);
  }

  const res = await request<Module | ApiEnvelope<Module>>({
    method: "POST",
    url: MODULES_BASE,
    data: finalBody,
  });

  return unwrap<Module>(res);
}

// 🔹 LIST (paginated)
// Backend is responsible for returning only active / non-deleted modules.
export async function listModules(
  query: ListModulesQuery = {}
): Promise<PaginatedModules> {
  // clamp + sanitize
  const safePage = Math.max(1, Number(query.page ?? 1));
  const rawLimit = Number(query.limit ?? 10);
  const safeLimit = Math.max(
    1,
    Math.min(100, Number.isFinite(rawLimit) ? rawLimit : 10)
  );

  const url = `${MODULES_BASE}${qs({
    page: safePage,
    limit: safeLimit,
    q: query.q,
    // no isActive / includeDeleted flags – backend handles visibility
  })}`;

  const res = await request<
    PaginatedModules | (ApiEnvelope<Module[]> & { meta?: any })
  >({
    method: "GET",
    url,
  });

  // If server returns { ok, data, meta }
  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    const r = res as ApiEnvelope<Module[]> & { meta?: any };

    const items = Array.isArray(r.data) ? r.data : [];
    const meta = r.meta ?? {};

    return {
      items,
      page: Number(meta.page ?? safePage),
      pageSize: Number(meta.pageSize ?? meta.limit ?? safeLimit),
      total: Number(meta.total ?? items.length ?? 0),
    };
  }

  // Otherwise assume backend already returns PaginatedModules
  return res as PaginatedModules;
}

// 🔹 GET BY ID
export async function getModule(id: number): Promise<Module> {
  const res = await request<Module | ApiEnvelope<Module>>({
    method: "GET",
    url: `${MODULES_BASE}/${id}`,
  });
  return unwrap<Module>(res);
}

// 🔹 UPDATE
export async function updateModule(
  id: number,
  body: UpdateModuleBody
): Promise<Module> {
  const { slug: _ignoreSlug, isActive: _ignoreActive, ...dto } = body as any;

  const parentId =
    dto.parentId === "" || dto.parentId === undefined
      ? null
      : typeof dto.parentId === "string"
      ? Number(dto.parentId)
      : dto.parentId ?? null;

  const finalBody = {
    ...dto,
    parentId,
  };

  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug("[updateModule] id=", id, "payload →", finalBody);
  }

  const res = await request<Module | ApiEnvelope<Module>>({
    method: "PUT",
    url: `${MODULES_BASE}/${id}`,
    data: finalBody,
  });
  return unwrap<Module>(res);
}

// 🔹 DELETE (hard delete from UI perspective)
export async function deleteModule(
  id: number
): Promise<{ message: string }> {
  const res = await request<
    { message: string } | ApiEnvelope<{ message: string }>
  >({
    method: "DELETE",
    url: `${MODULES_BASE}/${id}`,
  });
  return unwrap<{ message: string }>(res);
}

/* =========================
 * BULK OPS
 * ========================= */

/**
 * Bulk delete is still allowed (e.g., from a multi-select table),
 * but there is no restore / activate / deactivate anymore on the UI.
 */
export async function bulkDeleteModules(ids: number[]) {
  const res = await request<
    { message: string; count?: number } | ApiEnvelope<{ message: string; count?: number }>
  >({
    method: "POST",
    url: `${MODULES_BASE}/bulk-delete`,
    data: { ids },
  });
  return unwrap<{ message: string; count?: number }>(res);
}

/* =========================================================
 * Extras for parent dropdowns (roots only)
 * ========================================================= */

/** Normalize to ensure a camel-cased `parentId` is always present. */
function normalizeModule(m: any): Module & { parentId: number | null } {
  const parent =
    typeof m.parentId === "number" || m.parentId === null
      ? m.parentId
      : typeof m.parent_id === "number" || m.parent_id === null
      ? m.parent_id
      : null;

  return {
    ...(m as Module),
    parentId: parent,
  };
}

/**
 * Convenience: fetch a large page of modules and return only top-level
 * modules (those with parentId == null), sorted by name.
 */
export async function listRootModules(
  opts: Omit<ListModulesQuery, "page" | "limit"> & { limit?: number } = {}
): Promise<Module[]> {
  const res = await listModules({
    page: 1,
    limit: Math.min(1000, Number(opts.limit ?? 1000)),
    q: opts.q,
  });

  const items = Array.isArray((res as PaginatedModules).items)
    ? (res as PaginatedModules).items
    : Array.isArray(res)
    ? (res as Module[])
    : [];

  return items
    .map(normalizeModule)
    .filter((m) => m.parentId == null)
    .sort((a, b) => String(a.name ?? "").localeCompare(String(b.name ?? "")));
}
